import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonFunctions,
} from '../../shared/Button';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import InlineHelpText from '../../shared/InlineHelpText';
import SvgXIcon from '../../svg/XIcon';
import {
  submitText,
  cancelText,
  nameText,
  searchText,
  unitNameText,
  unitsText,
} from '../../shared/SharedInternationalizations';
import HelpIcon from '../../shared/HelpIcon';
import UserGeneralInfoForm from './UserGeneralInfoForm';
import UserContactForm from './UserContactForm';
import SvgUpArrowIcon from '../../svg/UpArrowIcon';

export function UnconnectedUserAddEditForm(props) {
  const { onSubmit, unitArray, unitObj, user } = props;
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState('general');

  function showGeneral() {
    setCurrentPage('general');
  }
  function showContact() {
    setCurrentPage('contact');
  }
  function showUnits() {
    setCurrentPage('units');
  }

  // const usesCheckin = user.preferences ? true : "";
  const [activeUnitFilter, setActiveUnitFilter] = useState('');
  const [unitsFilter, setUnitsFilter] = useState('');
  /**
   * Function to set the search value for active filtering
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setParentFilter - Updates local state with the value typed into the search bar
   */
  const searchFilter = (event) => {
    const value = event.target.value;
    setActiveUnitFilter(value);
  };

  const [showActiveUnitHelpText, setShowActiveUnitHelpText] = useState(false);
  const activeUnitHelpTextRef = useRef();
  function toggleActiveUnitHelpText(value) {
    setShowActiveUnitHelpText(value);
  }
  const activeUnitHelpText = (
    <FormattedMessage
      id="addEditUserForm.activeUnitHelpText"
      defaultMessage="This is the user's active unit."
      description="Help text."
    />
  );
  const [showUnitsHelpText, setShowUnitsHelpText] = useState(false);
  const unitsHelpTextRef = useRef();
  function toggleUnitsHelpText(value) {
    setShowUnitsHelpText(value);
  }
  const unitsHelpText = (
    <FormattedMessage
      id="addEditUserForm.unitsHelpText"
      defaultMessage="Assign units to a user."
      description="Help text."
    />
  );

  const handleCancel = () => {
    navigate('../'); // Go back to parent route (/users)
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const activeUnitTable = (values) => {
    let active_unit = values.active_unit;
    if (typeof active_unit === 'string') {
      active_unit = parseInt(active_unit);
    }

    const initiallyFilteredUnits = values.units.filter((item) => {
      if (item === active_unit) {
        return false;
      } else {
        return true;
      }
    });
    // map over the units and put into the format that is required for the table component
    const activeUnitArray = initiallyFilteredUnits.map((unit) => {
      const assignedUnit = unitObj[unit];
      return {
        selector: {
          sortBy: assignedUnit.id,
          display: assignedUnit.id,
        },
        name: {
          sortBy: assignedUnit.name.toUpperCase(),
          display: assignedUnit.name,
        },
      };
    });

    let searchedUnits = [];
    // Take the array and filter based on the text entered in the search bar.
    if (activeUnitFilter !== '') {
      searchedUnits = activeUnitArray.filter((item) => {
        // Make the device name uppercase for case insensitivity
        const parent_unit = item.name.display.toUpperCase();
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        return parent_unit.includes(activeUnitFilter.toUpperCase());
      });
    } else {
      searchedUnits = activeUnitArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'name',
        display: nameText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="active_unit"
        selectorParamValue="id"
        sortParam="name"
        tableData={searchedUnits}
        selectorType={selectorTypes.radio}
        tableStyles="table__constrained table-layout__1-column--selector"
        data-test="add-user-active-unit-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @param {object} values - The current values entered in the form
   * @returns {object} - The table of parent units
   */
  const selectedUnitTable = (values) => {
    let table;
    let active_unit = values.active_unit;

    if (active_unit !== '') {
      if (typeof active_unit === 'string') {
        active_unit = parseInt(active_unit);
      }
      const filteredUnit = unitArray.filter((unit) => unit.id === active_unit);

      const unitRow = filteredUnit.map((unit) => {
        const removeButton = (
          <label>
            <Field
              className="visually-hidden"
              name="active_unit"
              component="input"
              type="radio"
              value=""
            />
            <SvgXIcon />
          </label>
        );
        return {
          button: {
            sortBy: '',
            display: removeButton,
          },
          name: {
            sortBy: unit.name.toUpperCase(),
            display: unit.name,
          },
        };
      });

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: false,

          display: (
            <FormattedMessage
              id="addEditUserForm.parentUnitSelectedListHeading"
              defaultMessage="Selected Unit"
              description="Header to sort the unit name by"
            />
          ),
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="name"
          tableData={unitRow}
          tableStyles="table__constrained table-layout__1-column--selector"
          data-test="add-user-selected-active-unit-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  function displayActiveUnit(values) {
    if (values.units.length > 0) {
      return (
        <div className="form__section-container form__section-container--grid margin-bottom-25">
          <div className="form__item-container">
            <div className="align-center">
              <label className="text-medium text-bold" htmlFor="active_unit">
                <FormattedMessage
                  id="addEditUserForm.activeUnitLabel"
                  defaultMessage="Active Unit:"
                  description="The active unit of the user"
                />
              </label>
              <HelpIcon
                onClick={() => activeUnitHelpTextRef.current.toggleHelpText()}
                toggled={showActiveUnitHelpText}
                styles="margin-left-5"
              />
            </div>
          </div>
          <div className="width-100 margin-bottom-10">
            <InlineHelpText
              ref={activeUnitHelpTextRef}
              toggleFunction={toggleActiveUnitHelpText}
              message={activeUnitHelpText}
            />
          </div>

          {selectedUnitTable(values)}

          <div className="table__header padding-10">
            <label className="text-medium text-bold" htmlFor="activeUnitFilter">
              {searchText}
            </label>

            <input
              type="text"
              id="activeUnitFilter"
              name="activeUnitFilter"
              onChange={searchFilter}
              className="message-table__filter-search-box margin-left-10"
              value={activeUnitFilter}
            />
          </div>

          {activeUnitTable(values)}
        </div>
      );
    } else {
      return '';
    }
  }

  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const unitsSearchFilter = (event) => {
    const value = event.target.value;
    setUnitsFilter(value);
  };

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const unitsTable = (values) => {
    const initiallyFilteredUnits = unitArray.filter((item) => {
      if (values.units.includes(item.id)) {
        return false;
      } else if (!item.is_active) {
        return false;
      } else {
        return true;
      }
    });
    // map over the units and put into the format that is required for the table component
    const unitsArray = initiallyFilteredUnits.map((unit) => {
      let checked = null;
      return {
        selector: {
          checked: checked,
          sortBy: unit.id,
          display: unit.id,
        },
        name: {
          sortBy: unit.name.toUpperCase(),
          display: unit.name,
        },
      };
    });

    let searchedUnits = [];
    // Take the array and filter based on the text entered in the search bar.
    if (unitsFilter !== '') {
      searchedUnits = unitsArray.filter((item) => {
        // Make the device name uppercase for case insensitivity
        const units = item.name.display.toUpperCase();
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        return units.includes(unitsFilter.toUpperCase());
      });
    } else {
      searchedUnits = unitsArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'name',
        display: unitNameText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="units"
        selectorParamValue="id"
        sortParam="name"
        tableData={searchedUnits}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__constrained table-layout__1-column--selector"
        // selected={parent_unit}
        data-test="add-user-assigned-unit-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const userSelectedUnitsTable = (values) => {
    let table;
    let unitsId = values.units;
    let unitRows;
    if (unitsId.length > 0) {
      if (unitsId) {
        const filteredUnits = unitsId.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(unitsId);
          }
          const userId = unitArray.filter((unit) => unit.id === thisId);
          return userId[0];
        });
        unitRows = filteredUnits.map((unit) => {
          const removeButton = (
            <label>
              <Field
                className="visually-hidden"
                name="units"
                component="input"
                type="checkbox"
                value={unit.id}
              />
              <SvgXIcon />
            </label>
          );
          return {
            button: {
              sortBy: '',
              display: removeButton,
            },
            name: {
              sortBy: unit.name.toUpperCase(),
              display: unit.name,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'name',
          display: unitNameText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="name"
          tableData={unitRows}
          tableStyles="table__constrained table-layout__1-column--selector"
          data-test="add-user-assigned-unit-selected-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  const contactInfoText = (
    <FormattedMessage
      id="addEditUserForm.contactInfoText"
      defaultMessage="Contact Info"
      description="Help text."
    />
  );
  const generalInfoText = (
    <FormattedMessage
      id="addEditUserForm.generalInfoText"
      defaultMessage="General Info"
      description="Help text."
    />
  );

  function errorDisplay(errors) {
    const errorArr = Object.values(errors);
    const errorDisplay = errorArr.map((e) => {
      return (
        <div
          key={e}
          className="alert_message--danger padding-10 margin-5 text-small">
          {e}
        </div>
      );
    });
    return errorDisplay;
  }

  let hasPhoto = false;
  if (user) {
    if (user.photo && Object.keys(user.photo).length > 0) {
      hasPhoto = true;
    }
  }

  let userInfo = {};

  userInfo = {
    id: user.id,
    username: user.username,
    title: user.title,
    first_name: user.first_name,
    last_name: user.last_name,
    middle_name: user.middle_name,
    preferred_name: user.preferred_name,
    suffix: user.suffix,
    date_of_birth: user.date_of_birth,
    gender: user.gender,
    email: user.email,
    phone: user.phone,
    // Field name used in UserContactForm.js 
    makeMassnotification: user.makeMassnotification,
    status: user.status,
    active_unit: user.active_unit,
    isStaff: user.preferences ? true : false,
    units: user.units || [],
    notes: user.notes,
    usesCheckin: user.checkin_profile ? true : false,
    hasProfile: user.preferences || user.checkin_profile ? true : false,
    hasEmailDevice: false,
    hasPhoneDevice: false,
    hasPhoto: hasPhoto,
  };

  return (
    <div>
      <Form
        data-test="add-user-form"
        onSubmit={onSubmit}
        keepDirtyOnReinitialize={true}
        initialValues={{
          ...userInfo,
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          errors,
          values,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="width-100"
            noValidate="novalidate">
            <Field
              className="visually-hidden"
              name="id"
              component="input"
              type="text"
            />
            <Field
              className="visually-hidden"
              name="hasProfile"
              component="input"
              type="checkbox"
            />
            <Field
              className="visually-hidden"
              name="photo"
              component="input"
              type="checkbox"
            />
            <div className={currentPage === 'general' ? '' : 'visually-hidden'}>
              <UserGeneralInfoForm values={values} user={user} />
            </div>
            <div className={currentPage === 'contact' ? '' : 'visually-hidden'}>
              <UserContactForm values={values} user={user} />
            </div>
            <div
              className={
                currentPage === 'staffPreferences' ? '' : 'visually-hidden'
              }></div>
            <div
              className={
                currentPage === 'CheckInPreferences' ? '' : 'visually-hidden'
              }>
              <UserContactForm values={values} />
            </div>
            <div className={currentPage === 'units' ? '' : 'visually-hidden'}>
              <React.Fragment>
                {displayActiveUnit(values)}

                <div className="form__section-container form__section-container--grid margin-bottom-10">
                  <div className="form__item-container">
                    <div className="align-center">
                      <label className="text-medium text-bold" htmlFor="units">
                        <FormattedMessage
                          id="addEditUserForm.assignUnitsLabel"
                          defaultMessage="Assigned Units:"
                          description="The units selected"
                        />
                      </label>
                      <HelpIcon
                        onClick={() =>
                          unitsHelpTextRef.current.toggleHelpText()
                        }
                        toggled={showUnitsHelpText}
                        styles="margin-left-5"
                      />
                    </div>
                  </div>
                  <div className="width-100 margin-bottom-10">
                    <InlineHelpText
                      ref={unitsHelpTextRef}
                      toggleFunction={toggleUnitsHelpText}
                      message={unitsHelpText}
                    />
                  </div>
                  {userSelectedUnitsTable(values)}

                  <div className="table__header padding-10">
                    <label
                      className="text-medium text-bold"
                      htmlFor="usersFilter">
                      {searchText}:
                    </label>

                    <input
                      type="text"
                      id="unitsFilter"
                      name="unitsFilter"
                      onChange={unitsSearchFilter}
                      className="message-table__filter-search-box margin-left-10"
                      value={unitsFilter}
                    />
                  </div>

                  {unitsTable(values)}
                </div>
              </React.Fragment>
            </div>
            <hr />
            <div className="form__section-container form__section-container--grid">
              {errorDisplay(errors)}
            </div>
            <div className="form__section-container form__section-container--spread">
              <div className="form__items-group-container">
                <button
                type="button"
                className="link link-text"
                onClick={handleCancel}
                data-test="add-user-form-cancel-link">
                {cancelText}
              </button>
              </div>
              <div className="form__items-group-container">
                <div
                  className={
                    currentPage === 'general'
                      ? 'align-center'
                      : 'visually-hidden'
                  }>
                  <Button
                    text={contactInfoText}
                    buttonType={buttonTypes.blue}
                    buttonSize={buttonSizes.small}
                    onClick={showContact}
                    rightIcon={<SvgUpArrowIcon />}
                    rightIconStyle="rotate-90-cw margin-left-5"
                    additionalStyles="no-margin"
                  />
                </div>
                <div
                  className={
                    currentPage === 'contact'
                      ? 'align-center'
                      : 'visually-hidden'
                  }>
                  <Button
                    text={generalInfoText}
                    buttonType={buttonTypes.blue}
                    buttonSize={buttonSizes.small}
                    onClick={showGeneral}
                    leftIcon={<SvgUpArrowIcon />}
                    leftIconStyle="rotate-90-ccw margin-right-5"
                    additionalStyles="no-margin"
                  />

                  <Button
                    text={unitsText}
                    buttonType={buttonTypes.blue}
                    buttonSize={buttonSizes.small}
                    onClick={showUnits}
                    rightIcon={<SvgUpArrowIcon />}
                    rightIconStyle="rotate-90-cw margin-left-5"
                    additionalStyles={'no-margin margin-left-15'}
                  />
                </div>
                <div
                  className={
                    currentPage === 'units' ? 'align-center' : 'visually-hidden'
                  }>
                  <Button
                    text={contactInfoText}
                    buttonType={buttonTypes.blue}
                    buttonSize={buttonSizes.small}
                    onClick={showContact}
                    leftIcon={<SvgUpArrowIcon />}
                    leftIconStyle="rotate-90-ccw margin-right-5"
                  />
                </div>
                <div
                  className={
                    currentPage === 'units' ? 'align-center' : 'visually-hidden'
                  }>
                  <Button
                    buttonFunction={buttonFunctions.submit}
                    text={submitText}
                    buttonType={buttonTypes.success}
                    buttonSize={buttonSizes.small}
                    additionalStyles={'no-margin margin-left-15'}
                    disabled={pristine || invalid}
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}
const mapStateToProps = (state, ownProps) => {
  const unitArray = Object.values(state.units.units);
  return {
    unitArray,
    unitObj: state.units.units,
  };
};

export default connect(mapStateToProps)(UnconnectedUserAddEditForm);
